import { CreateFormDto, FormBaseType, FormBasicsDto } from "@green0meter/api-client";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import { useCreateForm, useGetAvailableFormTemplates, useUpdateForm } from "@api";
import Dialog from "@components/common/Dialog";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  Stack,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@components/base";
import { useUserStore } from "@store/user";
import { DEFAULT_DATE_FORMAT } from "@constants";

type Inputs = {
  name: string;
  validFrom: Date | null;
  validTo: Date | null;
  enableForAllEntities: boolean;
  useExistingFormAsTemplate: boolean;
  existingFormId: string | null;
  customTemplateId: string | null;
};

interface FormBasicsDialog {
  open: boolean;
  onClose: () => void;
  formBasics?: FormBasicsDto;
  formBaseType?: FormBaseType;
}

const FormBasicsDialog: FC<FormBasicsDialog> = ({
  open,
  onClose,
  formBasics,
  formBaseType = FormBaseType.VSME,
}) => {
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);
  const { t } = useTranslation();

  const { user } = useUserStore();
  const { createForm, createFormEnabled, isPendingCreateForm, isSuccessCreateForm } =
    useCreateForm(user);
  const { updateForm, updateFormEnabled, isPendingUpdateForm, isSuccessUpdateForm } =
    useUpdateForm(user);
  const { availableFormTemplates } = useGetAvailableFormTemplates(
    user,
    formBaseType,
  );

  const { register, handleSubmit, control, reset, watch, setValue } =
    useForm<Inputs>({
      defaultValues: {
        name: formBasics?.name || "",
        validFrom: formBasics ? dayjs(formBasics.validFrom).toDate() : null,
        validTo: formBasics ? dayjs(formBasics.validTo).toDate() : null,
        useExistingFormAsTemplate: false,
        existingFormId: null,
        customTemplateId: null,
      },
    });
  const validFrom = watch("validFrom");

  const useExistingFormAsTemplate = watch("useExistingFormAsTemplate");

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    if (formBasics) {
      // edit
      updateForm({
        formId: formBasics.id,
        data: {
          name: data?.name,
          validFrom: data.validFrom ? dayjs(data?.validFrom).toISOString() : "",
          validTo: data?.validTo ? dayjs(data?.validTo).toISOString() : "",
        },
      });
    } else {
      // create new
      const payload: CreateFormDto = {
        name: data?.name,
        formType: formBaseType,

        validFrom: data.validFrom ? dayjs(data?.validFrom).toISOString() : "",
        validTo: data.validTo ? dayjs(data?.validTo).toISOString() : "",
      };

      if (data.useExistingFormAsTemplate && data.existingFormId) {
        payload.formId = data.existingFormId;
      }

      if (data.customTemplateId !== "default" && data.customTemplateId) {
        payload.customTemplateId = data.customTemplateId;
      }

      createForm(payload);
    }
  };

  useEffect(() => {
    if (open) {
      reset({
        name: formBasics?.name || "",
        validFrom: formBasics ? dayjs(formBasics?.validFrom).toDate() : null,
        validTo: formBasics ? dayjs(formBasics?.validTo).toDate() : null,
        useExistingFormAsTemplate: false,
        existingFormId: null,
      });
    }
  }, [open, formBasics, reset]);

  useEffect(() => {
    if (isSuccessCreateForm || isSuccessUpdateForm) {
      onClose();
    }
  }, [isSuccessCreateForm, isSuccessUpdateForm]);

  useEffect(() => {
    if (!useExistingFormAsTemplate) {
      setValue("existingFormId", null);
    }
  }, [useExistingFormAsTemplate]);

  return (
    createFormEnabled &&
    updateFormEnabled && (
      <Dialog isOpen={open} onClose={onClose}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box pt={3}>
            <form onSubmit={handleSubmit(onSubmit)} onReset={onClose}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography
                    variant="h5"
                    fontSize={24}
                    fontWeight={700}
                    gutterBottom
                  >
                    {formBasics
                      ? "Edit data collection form"
                      : "New data collection form"}
                  </Typography>
                  <Typography>
                    {formBasics
                      ? "Edit name or reporting period dates."
                      : "Create a form name and set reporting period dates."}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <TextField
                    slotProps={{
                      input: { ...register("name", { required: true }) },
                    }}
                    fullWidth
                    label="Form name"
                    autoFocus
                  />
                </Grid>
                <Grid size={6}>
                  <Controller
                    {...register("validFrom", { required: true })}
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <DatePicker
                        label="From"
                        onChange={(date) => onChange(date?.toDate() || null)}
                        onClose={() => setDateFromOpen(false)}
                        onOpen={() => setDateFromOpen(true)}
                        value={value ? dayjs(value) : null}
                        timezone="UTC"
                        inputRef={ref}
                        format={DEFAULT_DATE_FORMAT}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            onClick: () => setDateFromOpen(true),
                          },
                        }}
                        open={dateFromOpen}
                      />
                    )}
                  />
                </Grid>
                <Grid size={6}>
                  <Controller
                    {...register("validTo", { required: false })}
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <DatePicker
                        label="To"
                        onChange={(date) => onChange(date?.toDate() || null)}
                        onClose={() => setDateToOpen(false)}
                        onOpen={() => (validFrom ? setDateToOpen(true) : null)}
                        minDate={dayjs(validFrom)}
                        disabled={!validFrom}
                        value={value ? dayjs(value) : null}
                        timezone="UTC"
                        inputRef={ref}
                        format={DEFAULT_DATE_FORMAT}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            onClick: () => (validFrom ? setDateToOpen(true) : null),
                          },
                        }}
                        open={dateToOpen}
                      />
                    )}
                  />
                </Grid>
                {!formBasics && (
                  <Grid size={12}>
                    <Controller
                      {...register("enableForAllEntities", { required: false })}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          checked
                          disabled
                          control={<Checkbox {...field} />}
                          label="Enable this form for all entities automatically"
                        />
                      )}
                    />
                  </Grid>
                )}
                {availableFormTemplates && !isEmpty(availableFormTemplates) && (
                  <Grid size={12}>
                    <Controller
                      {...register("customTemplateId", { required: false })}
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel id="templateId-label">
                            {t("createForm.selectTemplate")}
                          </InputLabel>
                          <Select
                            labelId="templateId-label"
                            {...field}
                            onChange={(event) => field.onChange(event.target.value)}
                            displayEmpty
                            value={field.value ?? "default"}
                          >
                            <MenuItem value="default" key="default">
                              <em>{t("createForm.defaultTemplate")}</em>
                            </MenuItem>
                            {availableFormTemplates.map((formTemplate) => (
                              <MenuItem
                                key={formTemplate.id}
                                value={formTemplate.id}
                              >
                                {formTemplate.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}
                <Grid size={12} justifyContent={"flex-end"} container>
                  <Stack spacing={2} direction="row">
                    <Button variant="outlined" type="reset">
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        formBasics ? isPendingUpdateForm : isPendingCreateForm
                      }
                    >
                      {formBasics ? t("common.save") : t("common.start")}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          </Box>
        </LocalizationProvider>
      </Dialog>
    )
  );
};

export default FormBasicsDialog;
